import type { Address, InsuranceTerms, Parcel, Rate, ShipmentPreview } from '../types';
import {
  canonicalizeRetryApiIdentity,
  LEGACY_UNBOUND_API_IDENTITY,
} from './retryApiIdentity';

const STORAGE_KEY = 'easypost_prepared_shipment_v1';

export interface PreparedShipmentPurchase {
  readonly version: 1 | 2;
  readonly apiUrl: string;
  readonly preparedAt: string;
  readonly previewShipmentId: string;
  readonly toAddress: Address;
  readonly normalizedToAddress: Address;
  readonly fromAddress: Address;
  readonly parcel: Parcel;
  readonly rate: Rate;
  readonly insurance: InsuranceTerms | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isAddress(value: unknown): value is Address {
  if (!isRecord(value)) return false;
  return ['street1', 'city', 'state', 'zip'].every((field) => isNonEmptyString(value[field]));
}

function isParcel(value: unknown): value is Parcel {
  return isRecord(value) && typeof value.weight === 'number' && Number.isFinite(value.weight) && value.weight > 0;
}

function isRate(value: unknown): value is Rate {
  return isRecord(value)
    && ['id', 'carrier', 'service', 'rate', 'currency'].every((field) => isNonEmptyString(value[field]));
}

function isInsurance(value: unknown): value is InsuranceTerms | null {
  return value === null || (
    isRecord(value)
    && isNonEmptyString(value.amount)
    && Number.isFinite(Number(value.amount))
    && Number(value.amount) > 0
    && value.currency === 'USD'
  );
}

function hasPreparedShipmentFields(value: Record<string, unknown>): boolean {
  return isNonEmptyString(value.preparedAt)
    && Number.isFinite(new Date(value.preparedAt).getTime())
    && isNonEmptyString(value.previewShipmentId)
    && isAddress(value.toAddress)
    && isAddress(value.normalizedToAddress)
    && isAddress(value.fromAddress)
    && isParcel(value.parcel)
    && isRate(value.rate)
    && isInsurance(value.insurance);
}

function isPreparedShipment(value: unknown): value is PreparedShipmentPurchase {
  return isRecord(value)
    && value.version === 2
    && isNonEmptyString(value.apiUrl)
    && hasPreparedShipmentFields(value);
}

function isLegacyPreparedShipment(value: unknown): value is Record<string, unknown> {
  return isRecord(value)
    && value.version === 1
    && hasPreparedShipmentFields(value);
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function createPreparedShipment(
  preview: ShipmentPreview,
  toAddress: Address,
  fromAddress: Address,
  parcel: Parcel,
  rate: Rate,
  apiUrl: string,
  preparedAt = new Date().toISOString()
): PreparedShipmentPurchase {
  return clone({
    version: 2,
    apiUrl: canonicalizeRetryApiIdentity(apiUrl),
    preparedAt,
    previewShipmentId: preview.shipment_id,
    toAddress,
    normalizedToAddress: { ...toAddress, ...preview.to_address },
    fromAddress,
    parcel,
    rate,
    insurance: preview.insurance ?? null,
  });
}

export function savePreparedShipment(value: PreparedShipmentPurchase): boolean {
  try {
    if (!isPreparedShipment(value)) return false;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadPreparedShipment(): PreparedShipmentPurchase | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isLegacyPreparedShipment(parsed)) {
      // Preserve, surface, and block the ambiguous retry handle. It must not be
      // silently discarded or guessed onto whichever Worker is current.
      return clone({
        ...parsed,
        version: 1,
        apiUrl: LEGACY_UNBOUND_API_IDENTITY,
      }) as PreparedShipmentPurchase;
    }
    if (!isPreparedShipment(parsed)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return clone(parsed);
  } catch {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage itself is unavailable; fail closed without masking that fact.
    }
    return null;
  }
}

export function clearPreparedShipment(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // A successful purchase should still render even when storage is unavailable.
  }
}

export function getPreparedShipmentStorageKey(): string {
  return STORAGE_KEY;
}
